import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InitStateService } from 'src/app/core/service/init-state.service';
import { AuthBackendService } from '../../../domain/auth/service/auth-backend.service';
import { ModalButtons } from '../../../domain/shared/component/modal-container/modal-container.component';
import {
  BROADCAST_CHANNEL_LOGOUT,
  BROADCAST_CHANNEL_LOGOUT_MESSAGE,
  BROADCAST_CHANNEL_NAME,
} from '../sidenav/side-nav.component';
import { SessionExpirationService } from "../../../domain/auth/service/session-expiration.service";

@Component({
  selector: 'app-modal-stay',
  templateUrl: './modal-stay.component.html',
  styleUrls: ['./modal-stay.component.scss'],
})
export class ModalStayComponent implements OnInit {
  /** ID of the timer interval, use it to clear the timer when it is done. */
  private intervalID: NodeJS.Timeout;

  // when user makes any request to reset the logout countdown.
  broadcasterUpdate: BroadcastChannel;
  // when user makes log out it sends signal to another tabs to synchronize log out for all tabs where user was working
  broadcasterLogOut: BroadcastChannel;

  title: string;
  is = new IsModalStay();
  show = new ShowModalStay();


  modalButtons: ModalButtons = {
    dismiss: 'Sign out',
    apply: 'Stay in',
  };

  constructor(
    private initStateService: InitStateService,
    private router: Router,
    private authBackendService: AuthBackendService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.initBroadcastSubscription();
  }

  async apply(): Promise<void> {
    if (this.is.sessionExpired) {
      await this.logOut();
    } else {
      await this.refreshClientSession();
    }
  }

  /** Method used for triggering the functionality of the `broadcasterLogOut`'s subscribers */
  broadcastLogout(): void {
    this.broadcasterLogOut.postMessage(BROADCAST_CHANNEL_LOGOUT_MESSAGE);
  }

  async logOut(): Promise<void> {
    this.resetTimerData();
    await this.authBackendService.logout();
    this.broadcastLogout();
    this.close();
  }

  async goToLogin(): Promise<void> {
    await this.router.navigate(['/login']);
    this.close();
  }

  /**
   * Starts timer to logout.
   * Default time is 60 min for logout and 5 min for warning.
   *
   * @param logoutTime Time in ms before logging out.
   * @param warningTime Time in ms before pop-up warning.
   * @param warningRedColorTime Time in ms after which timer colors red.
   */
  private startTimer(
    logoutTime: number = 60 * 60 * 1000,
    warningTime: number = 5 * 60 * 1000,
    warningRedColorTime: number = 1 * 60 * 1000): void {
    if (this.isLoginPage()) {
      return;
    }

    // logout time: e.x. 11:15
    const deadline = new Date().getTime() + logoutTime;

    this.ngZone.runOutsideAngular(() => {
      // This will keep running in the background and update logout timer every second.
      this.intervalID = setInterval(() => {
        // current time: e.x. 10:45
        const now = new Date().getTime();
        // time till deadline: e.x. 30min 15s
        const timeLeft = deadline - now;

        this.defineTitle(timeLeft);

        // Show warning for session time running out.
        if (timeLeft < warningTime && !this.show.modalStay) {
          this.show.modalStay = true;
        }

        // Show warning for session time running out.
        if (timeLeft < warningRedColorTime && !this.is.redTitleEnabled) {
          this.is.redTitleEnabled = true;
        }

        // deadline check
        if (timeLeft < 1) {
          this.is.sessionExpired = true;
          this.modalButtons = {
            apply: 'Sign in',
          };
        }

        // trigger update to show the new tick (second) in the modal.
        if (timeLeft < warningTime) {
          this.cdr.detectChanges();
        }
      }, 1000);
    });
  }

  private restart(): void {
    if (this.show.expiredSessionModal) {
      return;
    }
    this.close();
    this.resetTimerData();
    this.startTimer();
  }

  private close(): void {
    this.show.modalStay = false;
    this.show.expiredSessionModal = false;
    this.is.sessionExpired = false;
    this.initModalButtons();
    this.cdr.detectChanges();
  }

  private async refreshClientSession(): Promise<void> {
    await this.initStateService.updateSession();
    this.close();
  }

  private resetTimerData(): void {
    clearInterval(this.intervalID);
    this.intervalID = null;
    this.is.sessionExpired = false;
    this.is.redTitleEnabled = false;
  }

  /**
   * Receives the signal to restart the logout counter (1 hour on 29.01.24).
   * The signal comes either from:
   * 1) the same browser tab
   * 2) the different browser tab
   * 3) the space.
   */
  private initBroadcastSubscription(): void {
    this.broadcasterUpdate = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    this.broadcasterLogOut = new BroadcastChannel(BROADCAST_CHANNEL_LOGOUT);
    this.broadcasterUpdate.onmessage = (): void => {
      this.restart();
    };
    this.broadcasterLogOut.onmessage = (message): void => {
      if (message.data === 'logout') {
        // if i am a leader, i do not show the expired session modal.
        // if i am a cheburek, i show expired session modal :sadge:.
        if (!SessionExpirationService.getAmISessionLeaderByLogout()) {
          this.show.expiredSessionModal = true;
        }
      }
    };
  }

  private defineTitle(timeLeft: number): void {
    // Seconds for the timer: e.x. 15s
    const seconds = Math.floor(((timeLeft / 1000) % 60));
    // Minutes for the timer: e.x. 30min
    const minutes = Math.floor((timeLeft / 1000) / 60);
    // String to display time left before logging out.

    if (minutes > 0) {
      this.title = minutes + ' minutes ' + seconds + ' seconds ';
    } else {
      this.title = seconds + ' seconds';
    }
  }

  private isLoginPage(): boolean {
    return this.router.url.includes('login');
  }

  private initModalButtons(): void {
    this.modalButtons = {
      dismiss: 'Sign out',
      apply: 'Stay in',
    };
  }
}

class IsModalStay {
  redTitleEnabled = false;
  sessionExpired = false;
}

class ShowModalStay {
  modalStay = false;
  expiredSessionModal = false;
}
